import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Heading,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Text,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

const LivestockAmountChart = ({ data }) => {
  const [groupBy, setGroupBy] = useState("CountryRegion");

  // จัดการการเปลี่ยนแปลงใน dropdown
  const handleGroupByChange = (e) => {
    setGroupBy(e.target.value);
  };

  // เตรียมข้อมูลสำหรับกราฟ
  const chartData = () => {
    // กรองข้อมูลตามกลุ่มที่เลือก
    const groupField = groupBy === "CountryRegion" ? "CountryRegion" :
                      groupBy === "DLDRegion" ? "DLDRegion" : "Province";
    const groups = [...new Set(data.map((item) => item[groupField]))].filter(Boolean);
    const livestockTypes = [...new Set(data.map((item) => item.LivestockType))].filter(Boolean);

    // สร้างข้อมูลสำหรับแต่ละกลุ่ม โดยแยกตามประเภทปศุสัตว์
    return groups.map((group) => {
      const dataPoint = { name: group };
      livestockTypes.forEach((type) => {
        const relevantData = data.filter(
          (item) => item[groupField] === group && item.LivestockType === type
        );
        const total = relevantData.reduce((sum, item) => sum + item.Quantity, 0);
        dataPoint[type] = total;
      });
      return dataPoint;
    });
  };

  // สร้างสีสำหรับแต่ละประเภทปศุสัตว์
  const livestockTypes = [...new Set(data.map((item) => item.LivestockType))].filter(Boolean);
  const colors = [
    "#16A085", // Green
    "#3498DB", // Blue
    "#E74C3C", // Red
    "#F1C40F", // Yellow
    "#9B59B6", // Purple
  ];

  // Tooltip ที่กำหนดเอง
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid #ccc"
          borderRadius="md"
          boxShadow="md"
        >
          <p>
            <strong>
              {groupBy === "CountryRegion" ? "ภูมิภาค" :
               groupBy === "DLDRegion" ? "ปศุสัตว์เขต" : "จังหวัด"}:
            </strong> {label}
          </p>
          {payload.map((entry) => (
            <p key={entry.name}>
              <strong>{entry.name}:</strong> {entry.value} ตัว
            </p>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
      p={5}
      mt={6}
      bg="white"
    >
      
        <StandardHeader
          title="กราฟจำนวนปศุสัตว์แยกตามพื้นที่"
          bgColor="iGreen.600"
          icon={faChartBar}
        />
      <FormControl m={5} maxW="300px">
        <FormLabel fontSize="xl" color="gray">
          กลุ่มตาม
        </FormLabel>
        <Select
          value={groupBy}
          onChange={handleGroupByChange}
          colorScheme="iBlue"
        >
          <option value="CountryRegion">ภูมิภาค</option>
          <option value="DLDRegion">ปศุสัตว์เขต</option>
          <option value="Province">จังหวัด</option>
        </Select>
      </FormControl>
      {chartData().length > 0 ? (
        <ResponsiveContainer width="100%" height={500}>
          <BarChart
            data={chartData()}
            margin={{ top: 40, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              interval={0}
              height={150}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              label={{
                value: "จำนวน (ตัว)",
                angle: -90,
                position: "insideLeft",
                offset: -5,
              }}
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              layout="horizontal"
              verticalAlign="top"
              align="right"
              wrapperStyle={{ paddingBottom: 10 }}
            />
            {livestockTypes.map((type, index) => (
              <Bar
                key={type}
                dataKey={type}
                name={type}
                fill={colors[index % colors.length]}
                barSize={50 / livestockTypes.length}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Text textAlign="center" color="gray.500" p={5}>
          ไม่มีข้อมูลสำหรับแสดงกราฟ
        </Text>
      )}
    </Box>
  );
};

export default LivestockAmountChart;